import { useMemo, useState } from 'react';
import { InlineError, Input, Skeleton } from '../../components/ui';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import { useTapPeople } from '../projects/hooks';
import {
  buildDealRows,
  filterAndSortDeals,
  inPipelineScope,
  PIPELINE_SHORT,
  shortDate,
  silentCount,
  starredCount,
} from './allDeals';
import { DealDrawer } from './DealDrawer';
import { placeLabel } from './kanbanColumns';
import { starChanges } from './starMove';
import {
  useAllDealStages,
  useAllDeals,
  useDealLastActivity,
  useDealSections,
  useUpdateDeal,
} from './hooks';
import type { PipelineScope } from './allDeals';
import { dealLoadMessage } from './queries';
import { isOverdue } from './types';
import { localIsoDate } from '../myTasks/types';
import { useOutboundEntities } from './outboundEntities';
import type { DealRow } from './allDeals';

/**
 * All Deals: both pipelines, one flat searchable table.
 *
 * Kyu asked for two things on 10 September: a tab holding every deal, and a
 * way to search, because "I can't search a deal here."
 *
 * Quietest first, because of what Alex asked for on the same call. He is not
 * scanning for a tier, he is scanning for the ones that went dark: "we send a
 * deck and then we just go dark for weeks and weeks at a time, if not months."
 * Sorting this way costs nothing and makes the tab answer his question as a
 * side effect of answering Kyu's.
 *
 * Clicking a deal opens the drawer HERE. The first cut linked out to the
 * deal's own pipeline board instead, and Kyu's reaction was immediate: "I
 * dont like it when it routes me back to CG or Partnerships tab." The drawer
 * needs the sections and stages of the deal's own pipeline, so both
 * pipelines' sections are fetched and the right one is picked per deal.
 */

/** Past this, a deal is called out rather than just listed. */
const QUIET_DAYS = 30;

export function AllDealsView() {
  const permissions = useMyPermissions();
  const canEdit = canEditModule(permissions.data, 'deals');
  const deals = useAllDeals();
  const stages = useAllDealStages();
  const lastActivity = useDealLastActivity();
  const outbound = useOutboundEntities();
  const tapPeople = useTapPeople(true);

  const updateCg = useUpdateDeal('cg_deals');
  const updatePartnerships = useUpdateDeal('partnerships');
  const cgSections = useDealSections('cg_deals');
  const partnershipSections = useDealSections('partnerships');
  const [openDealId, setOpenDealId] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [starredOnly, setStarredOnly] = useState(false);
  // The board being looked at. 'all' by default, because the whole point of
  // this screen is the one list that does not make you pick first.
  const [pipeline, setPipeline] = useState<PipelineScope>('all');
  const [error, setError] = useState('');

  const companyNames = useMemo(
    () => new Map(outbound.companies.map((company) => [company.id, company.label])),
    [outbound.companies],
  );
  const stageNames = useMemo(
    () => new Map((stages.data ?? []).map((stage) => [stage.id, stage.name])),
    [stages.data],
  );
  // The drawer's people list, same shape DealList builds.
  const people = useMemo(
    () =>
      (tapPeople.data ?? []).map((person) => ({
        id: `tap-person-${person.id}`,
        user_id: person.id,
        project_role: 'member' as const,
        display_name: person.display_name,
        email: person.email,
        avatar_url: person.avatar_url,
      })),
    [tapPeople.data],
  );
  const ownerNames = useMemo(
    () =>
      new Map(
        (tapPeople.data ?? []).map((person) => [
          person.id,
          person.display_name.trim() === '' ? person.email : person.display_name,
        ]),
      ),
    [tapPeople.data],
  );

  const rows = useMemo(
    () => buildDealRows(deals.data ?? [], lastActivity.data ?? new Map(), new Date()),
    [deals.data, lastActivity.data],
  );
  // The chosen board, before search and Starred narrow it. Every count on the
  // bar is taken from this rather than from `rows`, so switching to CG
  // rewrites the denominator instead of leaving it at the two-board total.
  const scoped = useMemo(() => inPipelineScope(rows, pipeline), [rows, pipeline]);
  const shown = useMemo(
    () => filterAndSortDeals(rows, query, companyNames, starredOnly, pipeline),
    [rows, query, companyNames, starredOnly, pipeline],
  );
  const openDeal = (deals.data ?? []).find((deal) => deal.id === openDealId);

  if (deals.isPending || stages.isPending) {
    return <Skeleton lines={6} label="Loading every deal" />;
  }
  if (deals.isError) {
    const cause = deals.error;
    return (
      <InlineError>{dealLoadMessage(cause instanceof Error ? cause.message : '')}</InlineError>
    );
  }

  function toggleStar(row: DealRow) {
    setError('');
    const mutation = row.deal.pipeline === 'cg_deals' ? updateCg : updatePartnerships;
    // Both pipelines' sections, so starChanges can find the right Starred
    // section for whichever pipeline this row belongs to.
    const sections = [...(cgSections.data ?? []), ...(partnershipSections.data ?? [])];
    mutation.mutate(
      { dealId: row.deal.id, changes: starChanges(row.deal, sections) },
      {
        onError: (cause: unknown) =>
          setError(dealLoadMessage(cause instanceof Error ? cause.message : '')),
      },
    );
  }

  const today = localIsoDate(new Date());
  const quiet = silentCount(scoped, QUIET_DAYS);
  // Counted on the scoped set but BEFORE the Starred toggle, deliberately.
  // This number is the reason to press the button, so deriving it from the
  // button's own output would make it read `shown.length` the moment it was
  // on, which tells nobody anything.
  const starred = starredCount(scoped);

  return (
    <>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="all-deals-bar">
        <Input
          label="Search deals"
          labelHidden
          type="search"
          placeholder="Search by deal or company"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {/* Star a deal on either board and it shows up here. This is the
            same set the CEO brief is fed from. */}
        <button
          type="button"
          className={`all-deals-starred-toggle${starredOnly ? ' is-on' : ''}`}
          aria-pressed={starredOnly}
          onClick={() => setStarredOnly((value) => !value)}
        >
          <span aria-hidden="true">{starredOnly ? '★' : '☆'}</span> Starred
          {starred === 0 ? null : <span className="all-deals-starred-n"> {starred}</span>}
        </button>
        {/* A scope, not a filter. Labelled from PIPELINE_SHORT so these can
            never drift from the badges in the Pipeline column below. */}
        <div className="view-toggle" role="group" aria-label="Pipeline">
          {(['all', 'cg_deals', 'partnerships'] as const).map((key) => (
            <button
              key={key}
              type="button"
              className="view-toggle-button"
              aria-pressed={pipeline === key}
              onClick={() => setPipeline(key)}
            >
              {key === 'all' ? 'All' : PIPELINE_SHORT[key]}
            </button>
          ))}
        </div>

        <p className="all-deals-count">
          {shown.length} of {scoped.length}
          {/* Said here rather than left to be noticed by scrolling. This is
              the number Alex was reacting to on the call. */}
          {quiet === 0 ? null : (
            <span className="all-deals-quiet">
              {' '}
              · {quiet} quiet {QUIET_DAYS}+ days
            </span>
          )}
        </p>
      </div>

      {shown.length === 0 ? (
        <p className="task-panel-text">
          {rows.length === 0
            ? 'No deals yet.'
            : scoped.length === 0
              ? `No deals on the ${pipeline === 'all' ? '' : PIPELINE_SHORT[pipeline]} board yet.`
              : starredOnly && query.trim() === ''
                ? 'Nothing is starred yet. Star a deal on either board and it shows up here.'
                : `Nothing matches “${query}”.`}
        </p>
      ) : (
        /* The card is what makes the alternating rows readable: their fill is
           a small step down from white and resolves to nothing on the page.
           Fill and no stroke, like the board's sections. See .all-deals-card. */
        <div className="all-deals-card">
          <table className="ui-table deal-table all-deals-table">
            <thead>
              <tr>
                <th>
                  <span className="ui-visually-hidden">In CEO brief</span>
                </th>
                <th>Deal</th>
                <th>Pipeline</th>
                <th>Stage</th>
                <th>Owner</th>
                <th>Next action</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.deal.id} className="deal-row">
                  <td className="deal-cell--brief">
                    <button
                      type="button"
                      className={
                        row.deal.in_ceo_brief ? 'deal-brief-star is-on' : 'deal-brief-star'
                      }
                      aria-pressed={row.deal.in_ceo_brief}
                      disabled={!canEdit}
                      title={
                        row.deal.in_ceo_brief
                          ? `Remove ${row.deal.name} from the CEO brief`
                          : `Show ${row.deal.name} in the CEO brief`
                      }
                      onClick={() => toggleStar(row)}
                    >
                      <span aria-hidden="true">{row.deal.in_ceo_brief ? '★' : '☆'}</span>
                      <span className="ui-visually-hidden">
                        {row.deal.in_ceo_brief ? 'In the CEO brief' : 'Not in the CEO brief'}
                      </span>
                    </button>
                  </td>
                  <th scope="row">
                    <button
                      type="button"
                      className="deal-name"
                      title={row.deal.name}
                      onClick={() => setOpenDealId(row.deal.id)}
                    >
                      {row.deal.name}
                    </button>
                  </th>
                  <td>
                    <span className="all-deals-pipeline">{PIPELINE_SHORT[row.deal.pipeline]}</span>
                  </td>
                  <td>
                    {/* placeOf's answer, not the raw stage. A closed deal reads
                      Won or Lost here, the same as the board, the drawer and
                      the List tab. See placeLabel for the bug this fixes. */}
                    {placeLabel(row.deal, stageNames)}
                  </td>
                  <td>
                    {row.deal.owner_id === null
                      ? 'Unassigned'
                      : (ownerNames.get(row.deal.owner_id) ?? 'Unassigned')}
                  </td>
                  <td>
                    {row.deal.next_action_date === null ? (
                      '—'
                    ) : isOverdue(row.deal.next_action_date, today) ? (
                      <span className="deal-next-overdue">{row.deal.next_action_date}</span>
                    ) : (
                      <span className="deal-next-plain">{row.deal.next_action_date}</span>
                    )}
                  </td>
                  <td className="all-deals-quiet-cell">
                    {/* The date, then how long ago. Never a date for "we cannot
                      tell": a dash reads as missing, which is what it is. */}
                    {row.daysSilent === null ? (
                      <span className="all-deals-unknown" title="Nothing logged on this deal yet">
                        —
                      </span>
                    ) : (
                      <span
                        className={row.daysSilent >= QUIET_DAYS ? 'all-deals-stale' : undefined}
                      >
                        {shortDate(row.lastActivityAt, new Date())}
                        <span className="all-deals-ago"> · {row.daysSilent}d</span>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openDeal === undefined ? null : (
        <DealDrawer
          key={openDeal.id}
          deal={openDeal}
          sections={
            (openDeal.pipeline === 'cg_deals' ? cgSections.data : partnershipSections.data) ?? []
          }
          stages={(stages.data ?? []).filter((stage) => stage.pipeline === openDeal.pipeline)}
          people={people}
          pipeline={openDeal.pipeline}
          onClose={() => setOpenDealId(null)}
        />
      )}
    </>
  );
}
